import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ScrollRail.module.scss';

/**
 * Horizontal scroll row with left/right arrow buttons — same look as the
 * Home page "Find deals from the brands you already love." carousel
 * (white round button, grey border, soft shadow). The left arrow only
 * shows once you've scrolled away from the start, the right arrow only
 * while there's more to scroll. Arrows are hidden below 640px, where
 * touch-swiping is the natural way to scroll.
 *
 * `className` styles the scrolling track itself (flex row, gap, etc.).
 */
const ScrollRail = ({ className = '', children, ariaLabel = 'items' }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const obs = new ResizeObserver(updateScrollState);
    obs.observe(el);
    return () => obs.disconnect();
  }, [updateScrollState, children]);

  const scroll = dir => {
    const el = scrollRef.current;
    if (!el) return;
    // One "page" minus a bit, so the next card is always partly in view.
    const step = Math.max(260, el.clientWidth * 0.8);
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
    setTimeout(updateScrollState, 400);
  };

  return (
    <div className={styles.carousel}>
      {canScrollLeft && (
        <button
          type="button"
          className={`${styles.arrowBtn} ${styles.arrowLeft}`}
          onClick={() => scroll('left')}
          aria-label={`Scroll ${ariaLabel} left`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      <div ref={scrollRef} onScroll={updateScrollState} className={`${styles.track} ${className}`}>
        {children}
      </div>
      {canScrollRight && (
        <button
          type="button"
          className={`${styles.arrowBtn} ${styles.arrowRight}`}
          onClick={() => scroll('right')}
          aria-label={`Scroll ${ariaLabel} right`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ScrollRail;
